import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  Search, ArrowLeft, Send, FileText, AlertTriangle, Users, ChevronDown,
} from "lucide-react";
import { AppModal, AppButton, ConfirmModal, EmptyState } from "../ui";
import apiClient from "../../utils/apiClient";
import { useBranding } from "../../contexts/BrandingContext";
import {
  extractTemplateVars, countTemplateVars, fillPreviewText, templateText,
} from "../../utils/whatsappTemplate";
import { ENQUIRY_VAR_GROUPS, dynamicVarLabel, resolveDynamicVar } from "./enquiryWhatsappVars";
import "./enquiry-whatsapp-modal.scss";

/**
 * EnquiryWhatsAppModal — send an approved WhatsApp template to the enquiries
 * selected on the list page, one or many.
 *
 * Two steps: pick a template, then map its variables. Each variable is either
 * Static (one literal for everyone) or Dynamic (an enquiry field, resolved
 * per-recipient on the backend). That is the same paramConfig contract the
 * student broadcast uses, so both audiences share one wire shape.
 *
 * The preview renders against a real selected lead rather than placeholder text,
 * because the whole risk of a bulk send is discovering the wording was wrong
 * after it has already gone out.
 *
 * Props:
 *   show, onHide
 *   enquiries  — the selected enquiry rows (need fullName/mobile/course/etc.)
 *   onSent(result) — called after a successful send
 *   showToast(message, type)
 */

/* The WhatsApp glyph itself — lucide's MessageCircle reads as generic chat, and
   this dialog is explicitly "you are sending on WhatsApp". */
const WhatsAppGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

/* Delivered double-tick, so the preview bubble reads as a real chat message. */
const TickIcon = () => (
  <svg width="16" height="11" viewBox="0 0 16 11" fill="currentColor" aria-hidden="true">
    <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .501.203.697.697 0 0 0 .546-.266l6.646-8.417a.497.497 0 0 0 .108-.299.441.441 0 0 0-.19-.374z" />
    <path d="M15.472.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-.75-.707-.916 1.15.881.881a.724.724 0 0 0 .501.203.697.697 0 0 0 .546-.266l6.646-8.417a.497.497 0 0 0 .108-.299.441.441 0 0 0-.19-.374z" />
  </svg>
);

const groupsOf = (vars) => [
  { group: "header", label: "Header", params: vars.header },
  { group: "body", label: "Message body", params: vars.body },
  { group: "button", label: "Buttons", params: vars.button },
];

const EnquiryWhatsAppModal = ({ show, onHide, enquiries = [], onSent, showToast }) => {
  const branding = useBranding();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  // Keyed `${group}_${param.key}`, e.g. "body_1" / "button_btn_0_1".
  const [paramModes, setParamModes] = useState({});
  const [paramValues, setParamValues] = useState({});
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // ── Recipients ───────────────────────────────────────────────────────────
  // A lead with no usable mobile can't be messaged; surface that up front
  // instead of letting the backend silently skip them.
  const { eligible, skipped } = useMemo(() => {
    const ok = [];
    const no = [];
    (enquiries || []).forEach((e) => {
      const digits = String(e?.mobile || "").replace(/\D/g, "");
      (digits.length >= 10 ? ok : no).push(e);
    });
    return { eligible: ok, skipped: no };
  }, [enquiries]);

  const sampleEnquiry = eligible[0] || enquiries[0] || null;

  /* Institute-level values for the preview. Only what the browser can know:
     branding carries the names, the clock carries the dates. Institute contact
     details and the portal link resolve server-side, so they preview as their
     bracketed label — the same "will be filled in" cue an empty lead field gets. */
  const previewContext = useMemo(() => {
    const now = new Date();
    const opts = (o) => now.toLocaleString("en-IN", o);
    return {
      instituteName: branding?.brandName || "",
      brandName: branding?.brandName || "",
      legalName: branding?.legalName || branding?.brandName || "",
      currency: "INR",
      currentDate: now.toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }),
      currentTime: opts({ hour: "2-digit", minute: "2-digit", hour12: true }),
      currentDay: opts({ weekday: "long" }),
      currentMonth: opts({ month: "long" }),
      currentYear: String(now.getFullYear()),
      tomorrowDate: new Date(now.getTime() + 864e5)
        .toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }),
    };
  }, [branding]);

  const reset = useCallback(() => {
    setSelected(null);
    setParamModes({});
    setParamValues({});
    setSearch("");
    setConfirming(false);
  }, []);

  // ── Load approved templates on open ──────────────────────────────────────
  useEffect(() => {
    if (!show) { reset(); return; }
    let active = true;
    setLoading(true);
    setLoadError("");
    apiClient.get("/whatsapp/templates")
      .then(({ data }) => {
        if (!active) return;
        setTemplates((data.templates || []).filter(
          (t) => String(t.status || "").toUpperCase() === "APPROVED",
        ));
      })
      .catch((err) => {
        if (!active) return;
        setTemplates([]);
        setLoadError(err?.response?.data?.message || "Couldn't load templates.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show, reset]);

  const vars = useMemo(() => extractTemplateVars(selected), [selected]);
  const totalVars = countTemplateVars(vars);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) => t.name.toLowerCase().includes(q) || String(t.category || "").toLowerCase().includes(q),
    );
  }, [templates, search]);

  // ── Template selection ───────────────────────────────────────────────────
  const handleSelect = (tpl) => {
    setSelected(tpl);
    // Default every slot to Static prefilled with Meta's example, so a template
    // with no personalisation is a two-click send.
    const modes = {};
    const values = {};
    const parsed = extractTemplateVars(tpl);
    groupsOf(parsed).forEach(({ group, params }) => {
      params.forEach((p) => {
        modes[`${group}_${p.key}`] = "static";
        values[`${group}_${p.key}`] = p.example || "";
      });
    });
    setParamModes(modes);
    setParamValues(values);
  };

  const setMode = (k, mode) => {
    setParamModes((m) => ({ ...m, [k]: mode }));
    // Values aren't interchangeable between modes (literal vs variable name).
    setParamValues((v) => ({ ...v, [k]: mode === "dynamic" ? "enquiry_name" : "" }));
  };

  // A dynamic slot is complete once a variable is chosen; a static one needs text.
  const allFilled = totalVars === 0 || groupsOf(vars).every(({ group, params }) =>
    params.every((p) => {
      const k = `${group}_${p.key}`;
      return (paramValues[k] || "").trim().length > 0;
    }),
  );

  const canSend = !!selected && allFilled && eligible.length > 0;

  // ── Preview against a real recipient ─────────────────────────────────────
  const previewValues = useMemo(() => {
    const out = {};
    groupsOf(vars).forEach(({ group, params }) => {
      params.forEach((p) => {
        const k = `${group}_${p.key}`;
        out[k] = paramModes[k] === "dynamic"
          ? resolveDynamicVar(paramValues[k], sampleEnquiry, previewContext)
          : paramValues[k];
      });
    });
    return out;
  }, [vars, paramModes, paramValues, sampleEnquiry, previewContext]);

  const previewHeader = fillPreviewText(templateText(selected, "HEADER"), vars.header, previewValues, "header");
  const previewBody = fillPreviewText(templateText(selected, "BODY"), vars.body, previewValues, "body");
  const previewFooter = templateText(selected, "FOOTER");
  const previewButtons = (selected?.components || [])
    .find((c) => String(c?.type || "").toUpperCase() === "BUTTONS")?.buttons || [];

  // ── Send ─────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const buildEntries = (params, group) => params.map((p) => {
        const k = `${group}_${p.key}`;
        const mode = paramModes[k] === "dynamic" ? "dynamic" : "static";
        return {
          // `token` (not `key`) so positional slots stay positional on the wire —
          // a URL-button {{1}} must not be sent as a named parameter.
          key: p.token,
          type: mode,
          variable: mode === "dynamic" ? (paramValues[k] || "") : "",
          value: mode === "static" ? (paramValues[k] || "") : "",
          ...(p.buttonIndex !== undefined ? { buttonIndex: p.buttonIndex } : {}),
        };
      });

      const payload = {
        enquiryIds: eligible.map((e) => e._id),
        templateName: selected.name,
        languageCode: selected.language || "en",
        confirmSend: true,
      };
      if (totalVars > 0) {
        payload.paramConfig = {
          header: buildEntries(vars.header, "header"),
          body: buildEntries(vars.body, "body"),
          button: buildEntries(vars.button, "button"),
        };
      }

      const { data } = await apiClient.post("/enquiries/whatsapp/send", payload);
      showToast?.(data.message || "WhatsApp messages queued", "success");
      setConfirming(false);
      onHide?.();
      onSent?.(data);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Failed to send WhatsApp messages", "error");
      setConfirming(false);
    }
    setSending(false);
  };

  // ── Footer ───────────────────────────────────────────────────────────────
  const footer = selected ? (
    <div className="eqwa-footer">
      <AppButton variant="ghost" size="sm" onClick={reset} disabled={sending}>
        <ArrowLeft size={14} /> Back
      </AppButton>
      <AppButton size="sm" className="eqwa-send" onClick={() => setConfirming(true)} disabled={!canSend || sending}>
        {sending ? <Spinner animation="border" size="sm" /> : <Send size={14} />}
        {` Send to ${eligible.length} lead${eligible.length === 1 ? "" : "s"}`}
      </AppButton>
    </div>
  ) : null;

  return (
    <>
      <AppModal
        show={show}
        onHide={sending ? undefined : onHide}
        title="Send WhatsApp message"
        icon={<WhatsAppGlyph />}
        size="lg"
        footer={footer}
        className="eqwa-modal"
      >
        {/* ── Recipient summary ──────────────────────────────────────────── */}
        <div className="eqwa-recipients">
          <span className="eqwa-recipients__count">
            <Users size={14} />
            <strong>{eligible.length}</strong> recipient{eligible.length === 1 ? "" : "s"}
          </span>
          {skipped.length > 0 && (
            <span className="eqwa-recipients__skipped" title={skipped.map((e) => e.fullName).join(", ")}>
              <AlertTriangle size={13} />
              {skipped.length} skipped — no valid mobile
            </span>
          )}
          {eligible.length > 0 && (
            <span className="eqwa-recipients__names">
              {eligible.slice(0, 3).map((e) => e.fullName || e.mobile).join(", ")}
              {eligible.length > 3 && ` +${eligible.length - 3} more`}
            </span>
          )}
        </div>

        {eligible.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle size={32} />}
            title="No messageable leads selected"
            description="None of the selected enquiries have a valid mobile number."
          />
        ) : loading ? (
          <div className="eqwa-loader"><Spinner animation="border" size="sm" /> Loading templates…</div>
        ) : !selected ? (
          /* ── Step 1: choose a template ──────────────────────────────── */
          <div className="eqwa-list">
            <div className="eqwa-search">
              <Search size={15} className="eqwa-search__icon" />
              <input
                className="eqwa-search__input"
                placeholder="Search templates"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            {filtered.length === 0 ? (
              <EmptyState
                icon={<FileText size={32} />}
                title={loadError || "No approved templates"}
                description={
                  loadError
                    ? "Check your WhatsApp connection under Settings, then try again."
                    : "WhatsApp only allows approved templates for first contact. Create one in the WhatsApp → Templates tab."
                }
              />
            ) : (
              <ul className="eqwa-tpl-list">
                {filtered.map((t) => (
                  <li key={`${t.name}:${t.language}`}>
                    <button type="button" className="eqwa-tpl" onClick={() => handleSelect(t)}>
                      <div className="eqwa-tpl__top">
                        <span className="eqwa-tpl__name">{t.name}</span>
                        <span className="eqwa-tpl__lang">{t.language}</span>
                        {t.category && <span className="eqwa-tpl__cat">{t.category}</span>}
                      </div>
                      {templateText(t, "BODY") && (
                        <div className="eqwa-tpl__body">{templateText(t, "BODY")}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          /* ── Step 2: map variables ──────────────────────────────────── */
          <div className="eqwa-fill">
            <div className="eqwa-fill__head">
              <span className="eqwa-tpl__name">{selected.name}</span>
              <span className="eqwa-tpl__lang">{selected.language}</span>
            </div>

            <div className="eqwa-cols">
              <div className="eqwa-cols__fields">
                {totalVars === 0 ? (
                  <p className="eqwa-no-vars">
                    This template has no variables — the same message goes to every selected lead.
                  </p>
                ) : (
                  groupsOf(vars).map(({ group, label, params }) => (
                    params.length > 0 && (
                      <div key={group} className="eqwa-group">
                        <div className="eqwa-group__label">{label}</div>
                        {params.map((p) => {
                          const k = `${group}_${p.key}`;
                          const mode = paramModes[k] || "static";
                          return (
                            <div key={k} className="eqwa-field">
                              <div className="eqwa-field__head">
                                <span className="eqwa-field__label">{p.label}</span>
                                <div className="eqwa-modes" role="group" aria-label="Value type">
                                  <button
                                    type="button"
                                    className={`eqwa-mode ${mode === "static" ? "is-active" : ""}`}
                                    onClick={() => setMode(k, "static")}
                                  >
                                    Static
                                  </button>
                                  <button
                                    type="button"
                                    className={`eqwa-mode ${mode === "dynamic" ? "is-active" : ""}`}
                                    onClick={() => setMode(k, "dynamic")}
                                  >
                                    Dynamic
                                  </button>
                                </div>
                              </div>

                              {mode === "dynamic" ? (
                                <div className="eqwa-select">
                                  <select
                                    value={paramValues[k] || ""}
                                    onChange={(e) => setParamValues((v) => ({ ...v, [k]: e.target.value }))}
                                  >
                                    <option value="">Choose a field…</option>
                                    {ENQUIRY_VAR_GROUPS.map(({ group, vars: groupVars }) => (
                                      <optgroup key={group} label={group}>
                                        {groupVars.map((v) => (
                                          <option key={v.value} value={v.value}>{v.label}</option>
                                        ))}
                                      </optgroup>
                                    ))}
                                  </select>
                                  <ChevronDown size={14} className="eqwa-select__icon" />
                                </div>
                              ) : (
                                <input
                                  className="eqwa-input"
                                  value={paramValues[k] || ""}
                                  onChange={(e) => setParamValues((v) => ({ ...v, [k]: e.target.value }))}
                                  placeholder={
                                    p.baseUrl
                                      ? `URL suffix after ${p.baseUrl}/`
                                      : p.example ? `e.g. ${p.example}` : "Enter value"
                                  }
                                />
                              )}

                              {mode === "dynamic" && paramValues[k] && (
                                <span className="eqwa-field__hint">
                                  Each lead gets their own {dynamicVarLabel(paramValues[k]).toLowerCase()}.
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ))
                )}
              </div>

              {/* ── Live preview ────────────────────────────────────────── */}
              <div className="eqwa-cols__preview">
                <div className="eqwa-preview__label">
                  Preview
                  {sampleEnquiry && (
                    <span className="eqwa-preview__for">as {sampleEnquiry.fullName || sampleEnquiry.mobile} sees it</span>
                  )}
                </div>
                {/* Chat wallpaper + outgoing bubble, so what the admin approves
                    looks like what the lead will actually receive. */}
                <div className="eqwa-chat">
                  <div className="eqwa-bubble">
                    {previewHeader && <div className="eqwa-bubble__header">{previewHeader}</div>}
                    {previewBody && <div className="eqwa-bubble__body">{previewBody}</div>}
                    {previewFooter && <div className="eqwa-bubble__footer">{previewFooter}</div>}
                    <div className="eqwa-bubble__meta">
                      <span>{new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                      <TickIcon />
                    </div>
                    {previewButtons.length > 0 && (
                      <div className="eqwa-bubble__buttons">
                        {previewButtons.map((b, i) => (
                          <span key={b.text || i} className="eqwa-bubble__btn">{b.text}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <p className="eqwa-preview__note">
                  Every message sent is logged to that lead's follow-up notes.
                </p>
              </div>
            </div>
          </div>
        )}
      </AppModal>

      <ConfirmModal
        show={confirming}
        onHide={() => setConfirming(false)}
        onConfirm={handleSend}
        /* ConfirmModal's overlay sits at z-index 1050, below AppModal's 1080 —
           without this it would render *behind* the modal that opened it. */
        overlayClassName="eqwa-confirm-overlay"
        title="Send WhatsApp message"
        message={
          <>
            Send template <strong>{selected?.name}</strong> to{" "}
            <strong>{eligible.length}</strong> lead{eligible.length === 1 ? "" : "s"}?
            {skipped.length > 0 && (
              <div className="mt-2" style={{ fontSize: "0.85rem" }}>
                {skipped.length} selected {skipped.length === 1 ? "lead has" : "leads have"} no valid
                mobile number and will be skipped.
              </div>
            )}
          </>
        }
        confirmText="Send now"
        cancelText="Cancel"
        variant="success"
        loading={sending}
      />
    </>
  );
};

export default EnquiryWhatsAppModal;
